import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { App as AntApp, Button, Modal } from 'antd';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import FileViewerLazy from '../components/FileViewerLazy';
import { AsyncButton, Chip, Skeletons, dt, RM } from '../components/kit';
import { buildLetterHtml, letterDocument } from '../lib/coAgencyLetterDoc';
import * as lettersApi from '../api/coAgencyLetters';
import { useAuth } from '../context/AuthContext';
import { letterCoaShareLabel, letterCidShareLabel } from '../lib/coAgencyShare';

// FULL-SCREEN · /letters/:id — co-agency letter detail, in the same three tabs the
// booking form uses:
//
//   Overview · what the arrangement is and where it stands
//   Document · every field of the letter, plus the letter itself
//   Status   · the lifecycle timeline and the counter-signed executed copy
//
// Module tabs are the sale / booking-form chip row, so all three record types are
// navigated identically.
const SEGS = ['overview', 'document', 'status'];
const SEG_LABEL = { overview: 'Overview', document: 'Document', status: 'Status' };

const blank = (v) => v === undefined || v === null || v === '' || v === 0;
const date = (v) => (v && dayjs(v).isValid() ? dayjs(v).format('DD/MM/YYYY') : '—');
const pct = (v) => (v == null ? '—' : `${Number(v)}%`);

export default function LetterDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const { agent: me } = useAuth();

  const [letter, setLetter] = useState(null);
  const [tab, setTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  const [docHtml, setDocHtml] = useState(null);
  const [fileView, setFileView] = useState(null); // { name, url } → in-app PDF/image viewer
  const [removeExecOpen, setRemoveExecOpen] = useState(false); // executed-copy delete confirm
  const [issueOpen, setIssueOpen] = useState(false); // issue confirm
  const fileRef = useRef(null);

  const load = () => lettersApi.getCoAgencyLettersById(id).then((r) => setLetter(r?.data ?? r)).catch((e) => message.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const status = String(letter?.status_raw || letter?.status || '').toUpperCase();
  const isVerified = status === 'VERIFIED';

  // Issuing is the agent's own call — nothing is submitted to anyone — but it is not a
  // small one: it puts the letter into force, drops the watermark, makes it selectable
  // on a sale, and closes it to further editing. Only HQ can undo it, by voiding.
  // Hence the confirm step, matching the booking form's.
  const issueLetter = async () => {
    await lettersApi.createCoAgencyLettersIssueById(id);
    message.success('Letter issued');
    setIssueOpen(false);
    await load();
  };

  // Open the authoritative letter document (HQ letterhead + body template + chop) in
  // the in-app viewer (mobile-safe — no popups). Print → Save as PDF from the viewer.
  // Watermarked until issued.
  const viewLetter = async () => {
    const r = await lettersApi.listCoAgencyLetterSettings();
    const settings = r?.data ?? r ?? {};
    setDocHtml(letterDocument(buildLetterHtml(letter, settings, me), `Co-Agency Letter - ${letter.letter_ref}`));
  };

  // Signed-URL upload of the counter-signed executed copy.
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const ct = file.type || 'application/octet-stream';
      const up = (await lettersApi.createCoAgencyLettersSignedDocumentById(id, { fileName: file.name, fileSize: file.size, contentType: ct }))?.data;
      if (!up?.uploadUrl) throw new Error('Could not get an upload URL');
      try {
        await axios.put(up.uploadUrl, file, { headers: { 'Content-Type': ct, 'x-upsert': 'false' } });
      } catch {
        throw new Error('Could not upload this file — please use a PDF or a JPG / PNG / WebP image.');
      }
      await lettersApi.createCoAgencyLettersSignedDocumentConfirmById(id, { storagePath: up.path, fileName: file.name, fileSize: file.size, contentType: ct });
      message.success('Executed copy uploaded');
      await load();
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Confirmed, as the admin confirms it: this is the counter-signed copy the co-agency
  // sent back, it is deleted from storage outright, and getting another one means
  // asking them again. One tap was too little between the agent and that.
  const removeExecuted = async () => {
    await lettersApi.deleteCoAgencyLettersSignedDocumentById(id);
    message.success('Executed copy removed');
    setRemoveExecOpen(false);
    await load();
  };

  // Document tab — grouped exactly as the create screen groups its cards.
  const sections = letter ? [
    ['Letter', [
      ['Date', date(letter.letter_date)],
      ['Type', letter.letter_type === 'RENT' ? 'Rent' : 'Sale'],
    ]],
    ['Property & price', [
      ['Property', letter.property_address || '—'],
      ['Price', blank(letter.price_rm) ? '—' : RM(letter.price_rm),
        letter.price_type === 'PSF' && letter.price_psf
          ? `${RM(letter.price_psf)}/psf × ${Number(letter.area_sqft).toLocaleString('en-MY')} sq ft`
          : null],
      ['SST', pct(letter.tax_percentage)],
    ]],
    ['Co-agency', [
      ['Name', letter.co_agency_name || letter.co_agency || '—'],
      ['Registration no', letter.co_agency_registration_no || '—'],
      ['Address', letter.co_agency_address || '—'],
      ['Tel', letter.co_agency_phone || '—'],
    ]],
    ['Handling agent', [
      ['Name', letter.co_agency_agent_name || '—'],
      ['Contact', letter.co_agency_agent_contact || '—'],
      ['REN no', letter.co_agency_agent_ren_no || '—'],
      ['Email', letter.co_agency_agent_email || '—'],
    ]],
    ['Commission split', [
      ['CID Realtors', letterCidShareLabel(letter)],
      ['Co-agency', letterCoaShareLabel(letter)],
    ]],
  ] : [];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Edit lives here and only here. It is the same affordance on every record in
          the app, so it belongs where the eye already looks for it rather than
          duplicated into the draft callout, whose one job is Issue. */}
      <AppBar
        back
        title={letter?.letter_ref || 'Letter'}
        sub={letter ? letter.co_agency : ''}
        right={letter ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {letter.editable && <span onClick={() => nav(`/letters/${id}/edit`)} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 13, fontWeight: 600 }}>Edit</span>}
            <Chip label={letter.status} />
          </div>
        ) : null}
      />

      {!letter ? (
        <div style={{ padding: 18 }}><Skeletons n={3} height={70} /></div>
      ) : (
        <>
          <div className="scrx" style={{ display: 'flex', gap: 8, padding: '14px 15px', overflowX: 'auto', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
            {SEGS.map((s) => (
              <div
                key={s}
                onClick={() => setTab(s)}
                style={{ flex: 'none', whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', background: tab === s ? 'var(--primary)' : 'var(--surface2)', color: tab === s ? '#fff' : 'var(--muted)', border: `1px solid ${tab === s ? 'var(--primary)' : 'var(--border)'}` }}
              >
                {SEG_LABEL[s]}
              </div>
            ))}
          </div>

          {/* ═══ OVERVIEW ═════════════════════════════════════════════════════ */}
          {tab === 'overview' && (
            <div className="scrx" style={pane}>
              {/* Where it stands leads the page — a draft is a letter with a move
                  still to make on it, and that outranks what the deal is worth. */}
              <StatusCallout
                letter={letter}
                status={status}
                onIssue={() => setIssueOpen(true)}
              />

              <div style={{ ...card, gap: 4 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--primary)' }}>
                  Co-agency {letter.letter_type === 'RENT' ? 'rental' : 'sale'}
                </div>
                {!blank(letter.price_rm) && (
                  <div style={{ fontSize: 27, fontWeight: 700, letterSpacing: '-.028em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
                    {RM(letter.price_rm)}
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                  {letter.property_address || '—'}
                </div>
              </div>

              <div style={sectionLabel}>Commission split</div>
              <div style={{ ...card, gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>CID Realtors · <b>{letterCidShareLabel(letter)}</b></span>
                  <span><b>{letterCoaShareLabel(letter)}</b> · {letter.co_agency}</span>
                </div>
                {/* The bar only means something when both sides are percentages of the
                    same fee. On a flat-sum letter CID's side is "whatever is left", which
                    has no width until the fee is known — so it is said in words instead. */}
                {letter.share_basis === 'AMOUNT' ? (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>
                    A flat fee to the co-agency. CID Realtors takes the balance of the professional fee.
                  </div>
                ) : (
                  <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, Math.max(0, Number(letter.cid_share) || 0))}%`, background: 'var(--primary)' }} />
                    <div style={{ flex: 1, background: 'var(--chip)' }} />
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  Professional fee subject to {pct(letter.tax_percentage)} SST.
                </div>
              </div>

              <div style={sectionLabel}>Parties</div>
              <div style={{ ...card, padding: '2px 13px' }}>
                <KV k="Co-agency" v={letter.co_agency_name || letter.co_agency || '—'} sub={letter.co_agency_registration_no} />
                <KV k="Co-agency tel" v={letter.co_agency_phone || '—'} />
                <KV
                  k="Handling agent"
                  v={letter.co_agency_agent_name || '—'}
                  sub={[letter.co_agency_agent_contact, letter.co_agency_agent_ren_no].filter(Boolean).join(' · ')}
                  last
                />
              </div>

              <div style={{ height: 6 }} />
            </div>
          )}

          {/* ═══ DOCUMENT ═════════════════════════════════════════════════════ */}
          {tab === 'document' && (
            <div className="scrx" style={pane}>
              {sections.map(([title, rows]) => (
                <Fragment key={title}>
                  <div style={sectionLabel}>{title}</div>
                  <div style={{ ...card, padding: '2px 13px' }}>
                    {rows.map(([k, v, sub], i) => <KV key={k} k={k} v={v} sub={sub} last={i === rows.length - 1} />)}
                  </div>
                </Fragment>
              ))}

              {/* The button that used to end this tab now lives in the action bar at
                  the bottom of the page, where all three tabs can reach it. */}
              {!isVerified && (
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                  {status === 'PENDING'
                    ? 'Watermarked until you issue it — only an issued letter prints clean.'
                    : `Stamped ${status.toLowerCase()} — it no longer prints clean.`}
                </div>
              )}
              <div style={{ height: 6 }} />
            </div>
          )}

          {/* ═══ STATUS ═══════════════════════════════════════════════════════ */}
          {tab === 'status' && (
            <div className="scrx" style={pane}>
              <StatusCallout
                letter={letter}
                status={status}
                onIssue={() => setIssueOpen(true)}
              />

              <div style={sectionLabel}>Timeline</div>
              <div style={{ ...card, gap: 0 }}>
                {(letter.timeline || []).map((t, i, arr) => (
                  <div key={`${t.state}-${i}`} style={{ display: 'flex', gap: 14 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span style={{ width: 11, height: 11, borderRadius: 50, background: tlDot(t.state), flex: 'none', marginTop: 3 }} />
                      {i < arr.length - 1 && <span style={{ width: 1.5, flex: 1, background: 'var(--border)', minHeight: 16 }} />}
                    </div>
                    <div style={{ paddingBottom: i < arr.length - 1 ? 14 : 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.state}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{dt(t.at)}{t.note ? ` · ${t.note}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={sectionLabel}>Executed copy</div>
              <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={handleFile} style={{ display: 'none' }} />
              {letter.signed_document_url ? (
                <div style={{ ...card, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Counter-signed copy</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>uploaded</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
                    <Button size="small" onClick={() => setFileView({ name: `${letter.letter_ref || 'letter'} — executed copy`, url: letter.signed_document_url })}>View</Button>
                    <Button size="small" danger onClick={() => setRemoveExecOpen(true)}>Remove</Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button block loading={uploading} onClick={() => fileRef.current?.click()} style={{ height: 44, border: '1.5px dashed var(--border)', borderRadius: 12, background: 'transparent', color: 'var(--muted)', fontWeight: 600, fontSize: 13 }}>
                    {uploading ? 'Uploading…' : '↑ Upload counter-signed copy'}
                  </Button>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: -4 }}>
                    Once the co-agency has signed and returned the letter, upload it here so HQ has the executed copy on file.
                  </div>
                </>
              )}

              <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', color: 'var(--muted)' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11 V8 a4 4 0 0 1 8 0 v3" /></svg>
                  HQ-ONLY · READ-ONLY
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Voiding an issued letter is an HQ action. You’ll be notified if that happens.</div>
              </div>
              <div style={{ height: 6 }} />
            </div>
          )}

          {/* Reading the letter is the point of the whole record, so it is a fixed
              action bar rather than the last thing on one of three tabs — agents were
              switching to Document and scrolling past every field to find it, and
              reported not finding it at all. The label carries the state, so a draft
              can never be mistaken for the real thing. */}
          <div style={actionBar}>
            <AsyncButton type="primary" block onClick={viewLetter} onError={(e) => message.error(e.message)}>
              {isVerified
                ? `View letter · ${letter.letter_ref}`
                : status === 'PENDING'
                  ? 'Preview letter · watermarked'
                  : `View letter · stamped ${status.toLowerCase()}`}
            </AsyncButton>
          </div>
        </>
      )}

      {/* In-app letter document (popup-safe on mobile) */}
      {docHtml && <DocViewer title={letter?.letter_ref || 'Letter'} html={docHtml} onClose={() => setDocHtml(null)} />}

      {/* In-app executed-copy viewer */}
      {fileView && <FileViewerLazy title={fileView.name} url={fileView.url} onClose={() => setFileView(null)} />}

      {/* Issuing puts the letter into force and only HQ can undo it, so this lists what
          changes rather than asking "are you sure?" — same shape as the booking form's. */}
      <Modal open={issueOpen} title="Issue this letter?" footer={null} onCancel={() => setIssueOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            <b style={{ color: 'var(--text)' }}>{letter?.letter_ref}</b> takes effect from now.
            Nobody needs to verify it.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
            <li>The watermark comes off and the letter prints as the real document.</li>
            <li>
              A sale can then use it at the agreed{' '}
              <strong style={{ color: 'var(--text)' }}>{letterCidShareLabel(letter)} / {letterCoaShareLabel(letter)}</strong> split
              with {letter?.co_agency}.
            </li>
            <li>The terms are settled — the letter can no longer be edited.</li>
          </ul>
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>To undo it you would have to ask HQ to void the letter.</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
            <Button block onClick={() => setIssueOpen(false)}>Not yet</Button>
            <AsyncButton type="primary" block onClick={issueLetter} onError={(e) => message.error(e.message)}>Issue letter</AsyncButton>
          </div>
        </div>
      </Modal>

      {/* The executed copy is the co-agency's counter-signed return — deleted from
          storage outright, and replacing it means asking them for it again. */}
      <Modal open={removeExecOpen} title="Remove the executed copy?" footer={null} onCancel={() => setRemoveExecOpen(false)} destroyOnHidden centered>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            The counter-signed copy of <b style={{ color: 'var(--text)' }}>{letter?.letter_ref}</b> is deleted from storage.
            This cannot be undone — you would have to upload it again.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
            <Button block onClick={() => setRemoveExecOpen(false)}>Keep it</Button>
            <AsyncButton block danger onClick={removeExecuted} onError={(e) => message.error(e.message)}>Remove</AsyncButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Where the letter stands, stated rather than left to a chip colour. A draft is not
 * "done" — it is watermarked, no sale can use it, and issuing it is the agent's own
 * move — so each state says what it means and what happens next.
 */
function StatusCallout({ letter, status, onIssue }) {
  if (status === 'PENDING') {
    return (
      <div style={{ background: 'color-mix(in srgb, var(--primary) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--primary) 30%, transparent)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div style={{ display: 'flex', gap: 11 }}>
          <span style={{ flex: 'none', width: 20, height: 20, borderRadius: 50, background: 'var(--primary)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11.5, fontWeight: 800, marginTop: 1 }}>!</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Draft — not yet issued</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              It prints with a watermark and no sale can use it. Nobody needs to verify
              it — issue it once the terms are settled with the co-agency.
            </div>
          </div>
        </div>
        {/* Issue only — Edit is in the app bar, where every other record keeps it.
            Opens the confirm; the request itself runs from there. */}
        <Button type="primary" block onClick={onIssue} style={{ height: 42, borderRadius: 11 }}>
          Issue letter
        </Button>
      </div>
    );
  }
  if (status === 'VERIFIED') {
    return (
      <div style={{ background: 'color-mix(in srgb, var(--ok) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--ok) 30%, transparent)', borderRadius: 14, padding: '15px 16px', display: 'flex', gap: 11 }}>
        <span style={{ flex: 'none', width: 20, height: 20, borderRadius: 50, background: 'var(--ok)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11.5, fontWeight: 800, marginTop: 1 }}>✓</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Issued</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            The letter prints clean, can go to the co-agency for counter-signing, and is
            selectable on a sale.
          </div>
        </div>
      </div>
    );
  }
  if (status === 'VOIDED') {
    return (
      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>This letter has been voided</div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>It no longer applies to the deal. Raise a new letter if the arrangement still stands.</div>
      </div>
    );
  }
  return null;
}

// Colour the timeline dot by the state it represents, reusing the app's status-tone convention.
const tlDot = (state) => {
  const s = String(state || '').toUpperCase();
  if (/REJECT|VOID|CANCEL|WITHDRAW|EXPIRE/.test(s)) return 'var(--bad)';
  if (/VERIFI|APPROV|COMPLETE|SIGN|PAID|VALIDAT|ACTIVE/.test(s)) return 'var(--ok)';
  if (/PENDING|PARTIAL/.test(s)) return 'var(--warn)';
  return 'var(--primary)';
};

const pane = { flex: 1, overflowY: 'auto', padding: '16px 16px calc(20px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 12 };
// Pinned bottom action bar — same anatomy as the create screens' footer, so the
// primary move on a record sits where the primary move on a form does.
const actionBar = { padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none', display: 'flex', gap: 10 };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column' };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: -6 };

const KV = ({ k, v, sub, last }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '9px 0', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
    <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: 'none' }}>{k}</span>
    <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
      {v}{sub ? <span style={{ display: 'block', fontWeight: 400, color: 'var(--muted)', fontSize: 11.5 }}>{sub}</span> : null}
    </span>
  </div>
);
